"use client";

import {
    Check,
    ChevronDown,
    ChevronUp,
    Info,
    Trash2,
    User,
} from "lucide-react";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { AppButton, AppInput } from "@/components/design/forms";
import { useTranslations } from "next-intl";
import type { ParticipantFormData } from "./registrationFlowTypes";

export interface ParticipantCardHeaderProps {
    displayName: string;
    participant: ParticipantFormData;
    isValid: boolean;
    canRemove: boolean;
    isPrimaryProfile: boolean;
    primaryProfileLocked: boolean;
    primaryProfileMode: "choose" | "lockedOne" | "cabinetOwnerBadge" | "off";
    onToggle: () => void;
    onUpdate: (data: Partial<ParticipantFormData>) => void;
    onRemove: () => void;
    onSetPrimary: () => void;
    /** ЛК: нельзя переключать фестиваль / чемпионат */
    lockParticipationModeToggle?: boolean;
    hideExpandToggle?: boolean;
    /** Модалка: без сворачивания по клику на шапку */
    disableHeaderCollapseToggle?: boolean;
}

export function ParticipantCardHeader({
    displayName,
    participant,
    isValid,
    canRemove,
    isPrimaryProfile,
    primaryProfileLocked,
    primaryProfileMode,
    onToggle,
    onUpdate,
    onRemove,
    onSetPrimary,
    lockParticipationModeToggle = false,
    hideExpandToggle = false,
    disableHeaderCollapseToggle = false,
}: ParticipantCardHeaderProps) {
    const t = useTranslations("strings.registrationFlow");
    const primaryCheckboxDisabled = primaryProfileMode !== "choose";
    const primaryCheckboxChecked =
        primaryProfileMode === "cabinetOwnerBadge"
            ? true
            : isPrimaryProfile || primaryProfileLocked;
    return (
        <>
            {/* Card Header */}
            <div
                className={`flex w-full min-w-0 max-w-full items-start justify-between gap-2 px-3 py-3.5 hover:bg-fest-canvas transition-colors rounded-t-xl sm:gap-3 sm:px-5 ${
                    disableHeaderCollapseToggle
                        ? "cursor-default"
                        : "cursor-pointer"
                }`}
            >
                <div
                    className="flex min-w-0 flex-1 items-start gap-3"
                    onClick={
                        disableHeaderCollapseToggle ? undefined : onToggle
                    }
                >
                    <div
                        className={`mt-0.5 h-9 w-9 shrink-0 rounded-lg flex items-center justify-center transition-all ${
                            isValid
                                ? "bg-fest-apple-green text-white"
                                : "bg-fest-canvas text-fest-subtle-text"
                        }`}
                    >
                        {isValid ? (
                            <Check className="w-4.5 h-4.5" />
                        ) : (
                            <User className="w-4.5 h-4.5" />
                        )}
                    </div>
                    <div className="min-w-0 flex-1 text-left">
                        <h3 className="truncate text-sm font-bold text-fest-heading">
                            {displayName}
                        </h3>

                        {/* Festival | toggle | championship: single row, fixed 16×16 info hit targets */}
                        <div
                            className="mt-1.5 flex max-w-full flex-nowrap items-center gap-2 overflow-x-auto pb-0.5 sm:gap-2.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex shrink-0 items-center gap-1.5">
                                <span
                                    className={`whitespace-nowrap text-[10px] sm:text-xs font-medium leading-none transition-colors ${
                                        !participant.isChampionshipParticipant
                                            ? "text-fest-heading"
                                            : "text-fest-subtle-text"
                                    }`}
                                >
                                    {t("modeFestivalShort")}
                                </span>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <button
                                            type="button"
                                            className="inline-flex h-4 w-4 shrink-0 cursor-help items-center justify-center rounded-sm text-fest-subtle-text outline-none transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-fest-gold/40 focus-visible:ring-offset-1"
                                            aria-label={t("modeFestivalTooltip")}
                                        >
                                            <Info
                                                className="pointer-events-none h-3 w-3"
                                                aria-hidden
                                            />
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent
                                        side="top"
                                        className="bg-fest-heading text-white px-3 py-2 text-xs max-w-xs"
                                    >
                                        <p>{t("modeFestivalTooltip")}</p>
                                    </TooltipContent>
                                </Tooltip>
                            </div>

                            <AppButton
                                type="button"
                                disabled={lockParticipationModeToggle}
                                onClick={() =>
                                    onUpdate({
                                        isChampionshipParticipant:
                                            !participant.isChampionshipParticipant,
                                    })
                                }
                                className={`relative h-6 w-12 shrink-0 rounded-full transition-all duration-300 ${
                                    participant.isChampionshipParticipant
                                        ? "bg-fest-burgundy"
                                        : "bg-fest-gold"
                                } ${
                                    lockParticipationModeToggle
                                        ? "cursor-not-allowed opacity-60"
                                        : ""
                                }`}
                            >
                                <div
                                    className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow-md transition-transform duration-300 ${
                                        participant.isChampionshipParticipant
                                            ? "translate-x-6"
                                            : "translate-x-0"
                                    }`}
                                />
                            </AppButton>

                            <div className="flex shrink-0 items-center gap-1.5">
                                <span
                                    className={`whitespace-nowrap text-[10px] sm:text-xs font-medium leading-none transition-colors ${
                                        participant.isChampionshipParticipant
                                            ? "text-fest-heading"
                                            : "text-fest-subtle-text"
                                    }`}
                                >
                                    {t("modeChampionshipShort")}
                                </span>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <button
                                            type="button"
                                            className="inline-flex h-4 w-4 shrink-0 cursor-help items-center justify-center rounded-sm text-fest-subtle-text outline-none transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-fest-gold/40 focus-visible:ring-offset-1"
                                            aria-label={t("modeChampionshipTooltip")}
                                        >
                                            <Info
                                                className="pointer-events-none h-3 w-3"
                                                aria-hidden
                                            />
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent
                                        side="top"
                                        className="bg-fest-heading text-white px-3 py-2 text-xs max-w-xs"
                                    >
                                        <p>{t("modeChampionshipTooltip")}</p>
                                    </TooltipContent>
                                </Tooltip>
                            </div>
                        </div>
                        {participant.email && (
                            <p className="mt-0.5 break-all text-xs text-fest-subtle-text sm:break-words">
                                {participant.email}
                            </p>
                        )}
                        {!participant.isExpanded && isValid && (
                            <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-xs text-fest-apple-green font-medium">
                                    {t("statusComplete")}
                                </span>
                                <span className="text-xs text-fest-gold font-medium">
                                    {t("statusClickToEdit")}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex shrink-0 items-center gap-2 self-center">
                    {primaryProfileMode !== "off" && (
                    <div
                        className="flex max-w-[min(100%,11rem)] flex-shrink-0 items-center sm:max-w-none"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <label
                                    className={`flex items-center gap-2 rounded-lg border border-fest-border-warm bg-white/90 px-2.5 py-1.5 transition-colors ${
                                        primaryCheckboxDisabled
                                            ? "cursor-default opacity-90"
                                            : "cursor-pointer hover:border-fest-gold/60"
                                    }`}
                                >
                                    <AppInput
                                        type="checkbox"
                                        checked={primaryCheckboxChecked}
                                        disabled={primaryCheckboxDisabled}
                                        onChange={() => {
                                            if (!primaryCheckboxDisabled)
                                                onSetPrimary();
                                        }}
                                        className="h-4 w-4 rounded border-fest-gold accent-fest-gold disabled:cursor-not-allowed"
                                    />
                                    <span className="text-xs font-medium text-fest-heading whitespace-nowrap">
                                        {t("primaryCheckboxLabel")}
                                    </span>
                                </label>
                            </TooltipTrigger>
                            <TooltipContent
                                side="bottom"
                                className="max-w-xs bg-fest-heading px-3 py-2 text-xs text-white"
                            >
                                <p>{t("primaryTooltip")}</p>
                            </TooltipContent>
                        </Tooltip>
                    </div>
                    )}
                    {canRemove && (
                        <AppButton
                            onClick={(e) => {
                                e.stopPropagation();
                                onRemove();
                            }}
                            className="p-1.5 text-fest-subtle-text hover:text-fest-red hover:bg-fest-red/10 rounded-lg transition-colors"
                        >
                            <Trash2 className="w-4 h-4" />
                        </AppButton>
                    )}
                    {!hideExpandToggle && (
                        <AppButton onClick={onToggle} className="p-1">
                            {participant.isExpanded ? (
                                <ChevronUp className="w-5 h-5 text-fest-subtle-text" />
                            ) : (
                                <ChevronDown className="w-5 h-5 text-fest-subtle-text" />
                            )}
                        </AppButton>
                    )}
                </div>
            </div>
        </>
    );
}
